import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { DocsNav } from '@/components/docs/DocsNav';
import { DocsSidebarNav, SidebarSection } from '@/components/docs/DocsSidebarNav';
import { useDataTypeDocs } from '@/hooks/useDataTypeDocs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Hash, ArrowRight, Menu } from 'lucide-react';
import * as Icons from 'lucide-react';

const categoryOrder = ['format', 'type', 'structure', 'other'];

const categoryLabels: Record<string, string> = {
  format: 'Formats',
  type: 'Types',
  structure: 'Structures',
  other: 'Other',
};

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Calendar: Icons.Calendar,
  Clock: Icons.Clock,
  Key: Icons.Key,
  Phone: Icons.Phone,
  DollarSign: Icons.DollarSign,
  MapPin: Icons.MapPin,
  ToggleRight: Icons.ToggleRight,
  FileText: Icons.FileText,
  Hash: Icons.Hash,
  Mail: Icons.Mail,
  Link: Icons.Link,
  Globe: Icons.Globe,
  User: Icons.User,
};

export default function DataTypesIndex() {
  const { data: docs, isLoading, error } = useDataTypeDocs();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const groupedDocs = useMemo(() => {
    if (!docs) return {} as Record<string, typeof docs>;
    return docs.reduce((acc, doc) => {
      const category = doc.category || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(doc);
      return acc;
    }, {} as Record<string, typeof docs>);
  }, [docs]);

  // Build sidebar sections
  const sidebarSections: SidebarSection[] = useMemo(() => {
    if (!docs) return [];
    
    return categoryOrder
      .filter((cat) => groupedDocs[cat]?.length > 0)
      .map((category) => ({
        title: categoryLabels[category] || category,
        slug: category,
        items: groupedDocs[category]?.map((doc) => ({
          title: doc.name,
          slug: doc.slug,
          href: `/docs/data-types/${doc.slug}`,
        })) || [],
      }));
  }, [docs, groupedDocs]);

  const getIconComponent = (iconName: string | null) => {
    if (!iconName) return Icons.FileText;
    return iconMap[iconName] || Icons.FileText;
  };
  
  return (
    <Layout>
      <SEOHead path="/docs/data-types" />
      
      <DocsNav />
      
      <div className="container py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <DocsSidebarNav sections={sidebarSections} className="hidden lg:block" />
          
          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <Hash className="h-5 w-5 text-primary" />
                </div>
                <h1 className="text-3xl font-bold">Data Types</h1>
              </div>
              <p className="text-muted-foreground max-w-2xl">
                Reference documentation for all data formats, types, and structures 
                used across automotive data standards.
              </p>
            </div>
            
            {/* Loading State */}
            {isLoading && (
              <div className="space-y-8">
                {[1, 2].map((i) => (
                  <div key={i}>
                    <Skeleton className="h-6 w-40 mb-4" />
                    <div className="grid gap-4 md:grid-cols-2">
                      <Skeleton className="h-24" />
                      <Skeleton className="h-24" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <p className="text-destructive">Failed to load data types. Please try again.</p>
              </div>
            )}
            
            {/* Empty State */}
            {docs && docs.length === 0 && (
              <div className="text-center py-12 border rounded-lg bg-muted/20">
                <Hash className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No data types available</h3>
                <p className="text-muted-foreground">
                  Check back soon for data type documentation.
                </p>
              </div>
            )}
            
            {/* Categories with Types */}
            {docs && categoryOrder.map((category) => {
              const categoryDocs = groupedDocs[category];
              if (!categoryDocs || categoryDocs.length === 0) return null;
              
              return (
                <section key={category} className="mb-10">
                  <div className="flex items-center gap-2 mb-4">
                    <Hash className="h-5 w-5 text-muted-foreground" />
                    <h2 className="text-xl font-semibold">{categoryLabels[category] || category}</h2>
                    <Badge variant="secondary" className="ml-2">
                      {categoryDocs.length} {categoryDocs.length === 1 ? 'type' : 'types'}
                    </Badge>
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    {categoryDocs.map((doc) => {
                      const IconComponent = getIconComponent(doc.icon);
                      
                      return (
                        <Link key={doc.id} to={`/docs/data-types/${doc.slug}`} className="group">
                          <Card className="h-full transition-all hover:shadow-md hover:border-primary/50">
                            <CardHeader className="pb-4">
                              <div className="flex items-start gap-3">
                                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                                  <IconComponent className="h-4 w-4 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-base flex items-center gap-2">
                                    {doc.name}
                                    <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                                  </CardTitle>
                                  {doc.short_description && (
                                    <CardDescription className="text-sm line-clamp-2 mt-1">
                                      {doc.short_description}
                                    </CardDescription>
                                  )}
                                  {doc.example_value && (
                                    <code className="text-xs font-mono bg-muted px-2 py-0.5 rounded mt-2 inline-block text-muted-foreground">
                                      {doc.example_value}
                                    </code>
                                  )}
                                </div>
                              </div>
                            </CardHeader>
                          </Card>
                        </Link>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </main>
        </div>
      </div>

      {/* Mobile floating nav */}
      <div className="lg:hidden fixed bottom-4 right-4 z-50">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button size="icon" className="rounded-full shadow-lg h-12 w-12">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle data types menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 pt-12">
            <h3 className="font-semibold mb-4 px-3">Data Types</h3>
            <DocsSidebarNav 
              sections={sidebarSections} 
              onItemClick={() => setSidebarOpen(false)}
            />
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
